import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MetaModel, SecondarySearch } from '@shared/models';
import { ChatEvent, Message, Participant, Room } from '@shared/factories';
import { ChatEventsService, ChatService, SessionStorageService, UserService } from '@shared/services';

@Component({
  selector:      'chats-list',
  templateUrl: './chats-list.component.html',
  host: { class: 'am-flex-column am-flex-1 overflow relative' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ChatsListComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  @Input() unreadCount: number;

  rooms:         Room[] = [];
  meta:          MetaModel;
  search:        string;
  loading:       boolean;

  @Output() updateCount = new EventEmitter<number>();
  @Output() openRoom    = new EventEmitter<Room>();
  @Output() close       = new EventEmitter<void>();
  constructor(
    private chatService:           ChatService,
    private chatEventsService:     ChatEventsService,
    private sessionStorageService: SessionStorageService,
    private userService:           UserService
  ) { }

  ngOnInit(): void {
    this.sessionStorageService.pills.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(_ => this.reloadRooms());
    this.sessionStorageService.searchSecondaryValue.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(search => this.searchHandler(search, 'roomsList'));
    this.chatEventsService.chatEvents.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event  => this.chatEventsHandler(event));
    this.chatEventsService.rooms.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(rooms => this.updateRooms(rooms));
  }

  private searchHandler(search: SecondarySearch, useCase: string): void {
    if      (search?.useCase === useCase) this.search = search.query;
    else if (!search && this.search)    { this.search = null; this.reloadRooms(); }
  }

  private updateRooms(rooms: Room[]): void {
    rooms.forEach(room => {
      let roomIndex = this.rooms?.findIndex(r => r.id === room.id);
      if (roomIndex >= 0) this.rooms[roomIndex] = new Room(room.toJSON());
    });
  }

  reloadRooms(page: number = 1): void {
    this.loading = true;
    this.chatService.loadRooms(page, this.search).subscribe(
      ({ rooms, meta }) => {
        this.rooms = rooms;
        this.meta  = meta;
        this.chatEventsService.updateRooms(rooms);
        this.loading = null;
      },
      err => {
        console.error(err);
        this.loading = null;
      }
    );
  }

  closeList(): void {
    this.resetSearch();
    this.close.emit();
  }

  resetSearch(): void {
    if (this.search) {
      this.search = null;
      this.sessionStorageService.changeSearchSecondaryValue(null);
      this.reloadRooms();
    }
  }

  private chatEventsHandler(event: ChatEvent): void {
    switch (event?.type) {
      case 'join_room':
      case 'leave_room':                 { this.reloadRooms(this.meta?.current_page);   break; };
      case 'participant_online':         { this.participantOnlineHandler(event, true);  break; };
      case 'participant_offline':        { this.participantOnlineHandler(event, false); break; };
      case 'message_deleted':            { this.messageDeleteHandler(event);            break; };
      case 'bulk_message_create_result':
      case 'new_message':                { this.newMessageHandler(event);               break; };
      case 'bulk_message_update_result':
      case 'message_updated':            { this.messageUpdatedHandler(event);           break; };
      case 'seen_messages':              { this.messageSeenHandler(event);              break; };
      default:
        break;
    };
  }

  private newMessageHandler(event: ChatEvent): void {
    let roomIndex = this.rooms.findIndex(r => r.id === event.roomId);
    if (roomIndex >= 0 && this.meta?.current_page === 1) {
      let messageExists = this.rooms[roomIndex].messages?.findIndex(m => m.id === event.id);
      if (messageExists === -1) {
        this.rooms[roomIndex].messages.push(new Message({
          id:              event.id,
          participant_id:  event.participantId,
          bulk_message_id: event.bulkMessageId,
          created_at:      new Date().toISOString(),

          seq:             event.seq,
          text:            event.text,
          viewer_ids:     [event.participantId]
        }, this.rooms[roomIndex].meAsPartisipant.id));
        this.rooms[roomIndex] = new Room(this.rooms[roomIndex].toJSON());
        if (event.participantId !== this.rooms[roomIndex].meAsPartisipant.id) this.rooms[roomIndex].adjustUnread(1);
        this.chatEventsService.updateRooms([this.rooms[roomIndex]]);
      }
    } else this.reloadRooms(this.meta?.current_page);
  }

  private messageUpdatedHandler(event: ChatEvent): void {
    let roomIndex = this.rooms.findIndex(r => r.id === event.roomId);
    if (roomIndex >= 0) {
      let messageIndex = this.rooms[roomIndex].messages.findIndex(m => m.id === event.id);
      if (messageIndex >= 0) {
        this.rooms[roomIndex].messages[messageIndex].editedAt = new Date();
        this.rooms[roomIndex].messages[messageIndex].text     = event.text;
        this.chatEventsService.updateRooms([this.rooms[roomIndex]]);
      }
    }
  }

  private messageDeleteHandler(event: ChatEvent): void {
    let roomIndex = this.rooms.findIndex(r => r.id === event.roomId);
    if (roomIndex >= 0) {
      let room         = this.rooms[roomIndex];
      let messageIndex = room.activeMessages.findIndex(m => m.id === event.id);
      if (messageIndex >= 0) {
        let message         = room.activeMessages[messageIndex];
        let isLatestMessage = messageIndex + 1 === room.activeMessages.length;
        message.deletedAt   = new Date();
        if (!isLatestMessage) {
          this.chatEventsService.updateRooms([this.rooms[roomIndex]]);
          return void 0;
        } else if (room.activeMessages.length) {
          let prevMessageDate = room.activeMessages[messageIndex-1].createdAt;
          let { firstRoom                         } = this.roomsRange(this.rooms);
          let { firstRoomMessage, lastRoomMessage } = this.roomsRangeMessages(this.rooms);
          if (firstRoom.id !== room.id &&
              prevMessageDate.getTime() < firstRoomMessage?.getTime() &&
              prevMessageDate.getTime() >  lastRoomMessage?.getTime()) {
            this.chatEventsService.updateRooms([this.rooms[roomIndex]]);
            return void 0;
          }
        }
      }
    }
    this.reloadRooms(this.meta?.current_page);
  }

  private participantOnlineHandler(event: ChatEvent, state: boolean): void {
    let roomIndex = this.rooms.findIndex(r => r.id === event.roomId);
    if (roomIndex >= 0) {
      let participantIndex = this.rooms[roomIndex].participants.findIndex(p => p.id === event.participantId);
      if (participantIndex >= 0) {
        this.rooms[roomIndex].participants[participantIndex].isOnline = state;
        this.chatEventsService.updateRooms([this.rooms[roomIndex]]);
      }
    }
  }

  private createParticipant(event: ChatEvent): Participant {
    let participant = this.getRoomParticipant( event.roomId, event.participantId || event.id);
    return new Participant({
      id:          participant?.id       || event.id              || event.participantId,
      full_name:   participant?.fullName || event.participantName || (event.id ? this.userService.currentUserValue.name : null),
      is_owner:    participant?.isOwner  || event.isOwner         || false,
      am_user_id:  participant?.userId,

      deleted_at:  null,
      is_online:   true,

      added_by_id: null
    });
  }

  private joinToRoomHandler(event: ChatEvent): void {
    let existing = this.getRoomById(event.roomId);
    let { emptyRooms, availableRooms, joinedRooms } = this.roomsByState();
    if (this.roomsFilled(emptyRooms) || this.roomsFilled(availableRooms) || !existing) this.reloadRooms(this.meta?.current_page);
    else {
      this.setParticipantLeaveState(event, existing);
      let { firstRoomMessage, lastRoomMessage } = this.roomsRangeMessages(joinedRooms);
      let latestMessage = existing?.latestMessage?.createdAt;

      if (availableRooms.length) this.chatEventsService.updateRooms([existing]);
      else {
        if      (!firstRoomMessage || !latestMessage) this.reloadRooms(this.meta?.current_page);
        else if (!lastRoomMessage  ||
                  latestMessage.getTime() < firstRoomMessage.getTime() &&
                  latestMessage.getTime() >  lastRoomMessage.getTime()) this.chatEventsService.updateRooms([existing]);
        else this.reloadRooms(this.meta?.current_page);
      }
    }
  }

  private leaveRoomHandler(event: ChatEvent): void {
    let existing = this.getRoomById(event.roomId);
    let { emptyRooms, availableRooms, joinedRooms } = this.roomsByState();
    if (this.roomsFilled(emptyRooms) || this.roomsFilled(joinedRooms) || !existing) this.reloadRooms(this.meta?.current_page);
    else {
      this.setParticipantLeaveState(event, existing, new Date());
      let { firstRoomMessage, lastRoomMessage } = this.roomsRangeMessages(availableRooms);
      let latestMessage = existing?.latestMessage?.createdAt;

      if      (!firstRoomMessage || !latestMessage) this.reloadRooms(this.meta?.current_page);
      else if (!lastRoomMessage  ||
                latestMessage.getTime() < firstRoomMessage.getTime() &&
                latestMessage.getTime() >  lastRoomMessage.getTime()) this.chatEventsService.updateRooms([existing]);
      else this.reloadRooms(this.meta?.current_page);
    }
  }

  private setParticipantLeaveState(event: ChatEvent, room: Room, deleted: Date = null): void {
    let participantIndex = this.getParticipantIndex(event.roomId, event.participantId || event.id);
    if (participantIndex >= 0) room.participants[participantIndex].deletedAt = deleted;
    else if (!deleted)         room.participants.push(this.createParticipant(event));
  }

  private roomsByState(): any {
    return {
      emptyRooms:     this.rooms.filter(r => r.activeParticipants.length === 1),
      availableRooms: this.rooms.filter(r => !r.isParticipant),
      joinedRooms:    this.rooms.filter(r => r.isParticipant)
    };
  }

  private roomsFilled(rooms: Room[]): boolean {
    return rooms.length === this.meta.per_page;
  }

  private messageSeenHandler(event: ChatEvent): void {
    let room = this.getRoomById(event.roomId);
    if (room) {
      event.messageIds.forEach(mId => {
        let message = room.messages?.find(m => m.id === mId);
        if (message) message.viewerIds.push(event.participantId);

        let myId        = this.userService.currentUserValue.id;
        let participant = this.getRoomParticipant(event.roomId, event.participantId);
        if (participant?.userId === myId) {
          room.adjustUnread(-1);
          if (room.activeParticipants.find(p => p.userId === myId)) {
            --this.unreadCount;
            this.updateCount.emit(this.unreadCount);
          }
        }
      });
      this.chatEventsService.updateRooms([room]);
    }
  }

  private getRoomById(roomId: number): Room {
    return this.rooms?.find(r => r.id === roomId);
  }

  private getRoomParticipant(roomId: number, participantId: number): Participant {
    let room = this.getRoomById(roomId);
    return room?.participants?.find(p => p.id === participantId);
  }

  private getParticipantIndex(roomId: number, participantId: number): number {
    let room = this.getRoomById(roomId);
    return room?.participants?.findIndex(p => p.id === participantId);
  }

  private showNotification(room: Room): void {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') Notification.requestPermission();
    else {
      let notification = new Notification(
        'myTempton Chat', {
        icon: 'assets/images/icon/favicon.ico',
        body: `Neue Nachricht von ${room.roomName}`
      });

      notification.onclick = () => {
        window.focus();
        this.openRoom.emit(room);
      };
    }
  }

  sortedRooms(rooms: Room[] = this.rooms): Room[] {
    return rooms.sort((a, b) => {
      let group = this.roomGroup(a) - this.roomGroup(b);
      if (group) return group;
      return (b?.latestMessage?.createdAt?.getTime() || 0) - (a?.latestMessage?.createdAt?.getTime() || 0);
    });
  }

  private roomGroup(room: Room): number {
    if (!room.activeParticipants?.some(p => !p.isOwner) &&
         room.latestOwnerMessage?.viewerIds?.filter(v => v === room.owner.id)?.length === 0) return 0;
    if (room.isParticipant) return 1;
    return 2;
  }

  private roomsRange(rooms: Room[]): any {
    let sorted = this.sortedRooms(rooms);
    return {
      firstRoom: sorted[0],
      lastRoom:  sorted[sorted.length-1],
    };
  }

  private roomsRangeMessages(rooms: Room[]): any {
    let { firstRoom, lastRoom } = this.roomsRange(rooms);
    return {
      firstRoomMessage: firstRoom?.latestMessage?.createdAt,
      lastRoomMessage:  lastRoom?.latestMessage?.createdAt
    };
  }

}